/**
 * Running Google Ads from NL-AOS (pages/AdsCampaigns.tsx): types and the plain
 * labels shown for Google's statuses. No imports, so node --test loads this
 * file directly (test/campaigns-ui.test.js).
 */
export type Account = { customer_id: string; name: string; market?: string | null };
export type LiveCampaign = {
  id: string; name: string; status: "ENABLED" | "PAUSED" | string; primary_status: string | null;
  channel: string; bidding: string | null; daily_budget: number | null; shared_budget: boolean; editable: boolean;
  spend: number; clicks: number; impressions: number; conversions: number; cpa: number | null;
};
export type AdsChange = {
  id: number; approval_id: number | null; customer_id: string; kind: string; title: string;
  status: "applying" | "applied" | "failed" | "undone" | "undo_failed"; error: string | null;
  applied_at: string | null; undone_at: string | null; created_at: string; applied_by_name: string | null; undone_by_name: string | null; undoable: boolean;
};
export type WaitingChange = { approval_id: number; kind: string; title: string; customer_id: string; submitted_at: string; submitted_by_name: string | null };
export type CampaignsResponse = {
  accounts: Account[]; account?: Account; currency?: string; campaigns: LiveCampaign[]; stats_error?: string | null; error?: string;
  changes: AdsChange[]; waiting: WaitingChange[];
};
export type Place = { id: string; name: string; canonical_name: string; type: string };
export type SpecLocation = { type: "place"; id: string; name: string } | { type: "radius"; miles: number; address: { city?: string; state?: string; postal?: string }; name: string };
export type DayOfWeek = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";
export type CallHours = { days: DayOfWeek[]; start_hour: number; end_hour: number };
export type Sitelink = { text: string; description1: string; description2: string; final_url: string };
/** The SOP minimums and Google's limits (utils/adsCampaignBuilder.js LIMITS): [SOP minimum, Google maximum, characters]. */
export type Limits = {
  headlines: [number, number, number]; descriptions: [number, number, number]; keywords: number; negatives: number; path: number; name: number;
  ad_groups: number; ads: [number, number]; sitelinks: [number, number]; sitelink_text: number; sitelink_description: number;
  callouts: [number, number]; callout: number; snippet_values: [number, number]; snippet_value: number; min_daily_budget: number;
};
export type Defaults = {
  client: { id: number; name: string; industry: string | null };
  accounts: Account[]; final_url: string; phone: string; ctm_numbers: string[]; brand_terms: string[];
  locations: { id: number; name: string; city: string | null; state: string | null }[];
  saved_keywords: { keyword: string; tag: string | null }[];
  standard_negatives: string[]; daily_cap: number; call_hours: CallHours; limits: Limits;
  copy_rules: { forbidden: string[]; excluded: string[]; required_cta: string[]; spelling: string | null };
};
export type ProposeResult = { approval_id: number; status: "applied" | "pending"; message: string };
/** A form field's problem from the SOP check or Google ("ad_groups.0.ads.1", "sitelinks", "google"). */
export type FieldNote = { field: string; message: string };
export type CheckResult = { ok: boolean; errors: FieldNote[]; warnings?: FieldNote[]; summary?: Record<string, unknown> | null };
export type DraftAd = { style: "emotional" | "direct"; headlines: string[]; descriptions: string[]; problems: string[] };
export type DraftAssets = { callouts: string[]; snippet: { header: string; values: string[] }; sitelinks: Sitelink[]; message?: string };

export const DAYS: { id: DayOfWeek; short: string }[] = [
  { id: "MONDAY", short: "Mon" }, { id: "TUESDAY", short: "Tue" }, { id: "WEDNESDAY", short: "Wed" }, { id: "THURSDAY", short: "Thu" },
  { id: "FRIDAY", short: "Fri" }, { id: "SATURDAY", short: "Sat" }, { id: "SUNDAY", short: "Sun" },
];
/** 8 → "8am", 12 → "12pm", 24 → "Midnight". */
export const hourLabel = (h: number) => (h === 24 ? "Midnight" : h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`);

const norm = (s: string) => String(s || "").toLowerCase().replace(/[‘’']/g, "").replace(/[^a-z0-9$%&/+.\- ]+/g, " ").replace(/\s+/g, " ").trim();
/** A brand campaign (KB-004 §1 and §4): "brand" in its name, or half its keywords search for the business by name. The twin of utils/adsSop.js isBrandCampaign. */
export function isBrandCampaign(name: string, keywords: string[], terms: string[]) {
  if (/\bbrand(ed)?\b/i.test(name)) return true;
  const list = keywords.map(norm).filter(Boolean);
  return list.length > 0 && terms.length > 0 && list.filter((k) => terms.some((t) => k.includes(t))).length / list.length >= 0.5;
}
/** Same phone number, however it is written. */
export const samePhone = (a: string, b: string) => {
  const d = (v: string) => String(v || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  return !!d(a) && d(a) === d(b);
};

/** "9395081472" → "939-508-1472", as Google Ads shows it. */
export const formatCustomerId = (id: string) => String(id || "").replace(/^(\d{3})(\d{3})(\d{4})$/, "$1-$2-$3");

/** On or Paused: the switch a person flips. */
export const statusLabel = (status: string) => (status === "ENABLED" ? "On" : status === "PAUSED" ? "Paused" : status ? status[0] + status.slice(1).toLowerCase() : "");

/** Google's own serving verdict, said plainly; null when it adds nothing to On or Paused. */
export function servingNote(c: Pick<LiveCampaign, "status" | "primary_status">): string | null {
  const p = String(c.primary_status || "");
  if (!p || p === "ELIGIBLE" || p === "PAUSED" || (p === "UNKNOWN" && c.status === "PAUSED")) return null;
  const notes: Record<string, string> = {
    LIMITED: "Limited", LEARNING: "Learning", NOT_ELIGIBLE: "Not showing", MISCONFIGURED: "Needs setup",
    PENDING: "Starting soon", ENDED: "Ended", REMOVED: "Removed",
  };
  return notes[p] || p[0] + p.slice(1).toLowerCase().replace(/_/g, " ");
}

const BIDDING: Record<string, string> = {
  MAXIMIZE_CONVERSIONS: "Max conversions", TARGET_CPA: "Target CPA", MAXIMIZE_CONVERSION_VALUE: "Max conv. value",
  TARGET_ROAS: "Target ROAS", TARGET_SPEND: "Max clicks", MANUAL_CPC: "Manual CPC", TARGET_IMPRESSION_SHARE: "Impression share",
};
export const biddingLabel = (b: string | null) => (b ? BIDDING[b] || b[0] + b.slice(1).toLowerCase().replace(/_/g, " ") : "");

/** KB-029 §6: a budget moves at most 20% at a time. The same rounding as the server. */
export function budgetRange(current: number) {
  return { min: Math.ceil(current * 0.8 * 100) / 100, max: Math.floor(current * 1.2 * 100) / 100 };
}

const CHANGE_STATUS: Record<AdsChange["status"], { label: string; tone: "success" | "danger" | "neutral" | "warning" }> = {
  applying: { label: "Applying", tone: "neutral" }, applied: { label: "Applied", tone: "success" }, failed: { label: "Not applied", tone: "danger" },
  undone: { label: "Undone", tone: "neutral" }, undo_failed: { label: "Undo failed", tone: "danger" },
};
export const changeStatus = (s: AdsChange["status"]) => CHANGE_STATUS[s] || { label: s, tone: "neutral" as const };

/** Lines a textarea holds, trimmed, blanks dropped. */
export const lines = (text: string) => text.split("\n").map((l) => l.trim()).filter(Boolean);

/** The ad as Google shows it on a phone: up to three headlines, the display path, two descriptions. */
export function adPreview(finalUrl: string, path1: string, path2: string, headlines: string[], descriptions: string[]) {
  let host = "";
  try { host = new URL(finalUrl).hostname.replace(/^www\./, ""); } catch { host = finalUrl.replace(/^https?:\/\//, "").split("/")[0]; }
  const path = [path1, path2].map((p) => p.trim().replace(/[\s/]+/g, "-")).filter(Boolean);
  return {
    url: [host || "example.com", ...path].join(" › "),
    headline: headlines.filter((h) => h.length <= 30).slice(0, 3).join(" | ") || "Your headline",
    description: descriptions.filter((d) => d.length <= 90).slice(0, 2).join(" ") || "Your description",
  };
}
